import React from 'react';
import { Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import MarkdownCardPreview from './MarkdownCardPreview';
import { splitTrailingParenthetical } from '../utils/textFormat';
import { BenchmarkRun } from '../services/benchmarkService';
import WorkflowRunHealth from './ExecutionStats/WorkflowRunHealth';
import { InitialRuns } from './ExecutionStats/WorkflowRunPanel';
import { AttentionBucket } from './ExecutionStats/executionStatsVocabulary';
import { useWorkflowRunStats } from './ExecutionStats/useExecutionSummary';
import { ATTENTION_WINDOW } from './ProjectAttentionStrip';

export interface Campaign {
    id: string;
    campaign_name: string;
    campaign_description: string;
    /**
     * Denormalized by the campaigns API from the operator this campaign was
     * cloned from. Optional because it is filled in on read for campaigns
     * created before the field existed, and a campaign whose operator has been
     * deleted still has none.
     */
    flow_template_code?: string;
    operator_code: string;
    campaign_type?: string;
    campaign_type_name?: string;
}

/**
 * "12 Sep · 1,708 alerts" — enough to date a backtest.
 *
 * Missing figures are dropped rather than defaulted, so a run that recorded
 * neither a completion time nor a count gives an empty string.
 */
export const describeRunDetail = (run: BenchmarkRun): string => {
    const when = run.completedAt
        ? new Date(run.completedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
        : null;
    const alerts = Number.isFinite(Number(run.predictionCount))
        ? `${Number(run.predictionCount).toLocaleString()} alerts`
        : null;
    return [when, alerts].filter(Boolean).join(' · ');
};

/**
 * "Backtested 12 Sep · 1,708 alerts" — the same, as a sentence for a tooltip.
 *
 * A run that cannot be described still gets a label, because the run itself
 * is the thing being pointed at.
 */
export const describeRun = (run: BenchmarkRun): string => {
    const detail = describeRunDetail(run);
    return detail ? `Backtested ${detail}` : 'Backtested';
};

interface ProjectWorkflowCardProps {
    campaign: Campaign;
    latestRun?: BenchmarkRun;
    onOpen: () => void;
    onViewInsights: () => void;
    onCustomize: () => void;
    onViewBacktest: (run: BenchmarkRun) => void;
    /** Opens the drawer on the runs behind a chip rather than on the totals. */
    onOpenRuns: (runs: InitialRuns) => void;
    /** The page's attention filter, so the chip that matched it stands out. */
    highlightBucket?: AttentionBucket | null;
}

/**
 * One workflow in the project catalog.
 *
 * Sized like the operators marketplace's cards and stacked in type groups: the
 * Status tab gives the overview, so this is the catalog of workflows and their
 * actions, read by scanning. The card opens the drawer, which carries
 * the full run grid and the runs behind it.
 */
const ProjectWorkflowCard: React.FC<ProjectWorkflowCardProps> = ({
    campaign, latestRun, onOpen, onViewInsights, onCustomize, onViewBacktest, onOpenRuns, highlightBucket
}) => {
    const { primary: campaignTitle, secondary: campaignTagline } = splitTrailingParenthetical(campaign.campaign_name);
    // The page reads the same numbers for its roll-up, whichever tab is showing.
    // Batched and cached, so the two cost one request.
    const stats = useWorkflowRunStats({ campaignId: campaign.id, flowCode: campaign.flow_template_code });

    // Actions as buttons ahead of the run line: as bare text beside it they read
    // as metadata and were easy to miss. Insights is the card's main
    // destination, so it alone is filled, as in the drawer.
    const action = (label: string, icon: React.ReactNode, onClick: () => void, primary = false) => (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            // Share the row evenly while they fit; on a narrow card the last
            // one wraps and takes the full width rather than being clipped.
            className={`flex-1 basis-[6.5rem] inline-flex items-center justify-center gap-1.5 h-8 px-2.5 rounded-md whitespace-nowrap text-[13px] font-sans font-semibold border border-solid transition-colors cursor-pointer m-0 ${primary
                ? 'bg-accent border-accent text-on-accent hover:brightness-105'
                : 'bg-surface border-fg-2/40 text-fg-body hover:border-accent-fg hover:text-accent-fg'}`}
        >
            {icon}
            {label}
        </button>
    );

    return (
        <div
            className="group relative bg-page border border-line rounded-lg overflow-hidden hover:border-accent-fg/50 transition-all duration-300 m-0 cursor-pointer"
            onClick={onOpen}
        >
            <div className="p-5 flex flex-col gap-3 h-full m-0 text-left items-start">
                <div className="w-full min-w-0">
                    <h4 className="text-base font-bold text-fg leading-snug m-0 w-full font-mono line-clamp-2 group-hover:text-accent-fg transition-colors">{campaignTitle}</h4>
                    {campaignTagline && (
                        <p className="text-sm text-accent-fg/80 font-medium leading-snug m-0 mt-0.5 w-full font-sans truncate">{campaignTagline}</p>
                    )}
                </div>
                <MarkdownCardPreview
                    text={campaign.campaign_description}
                    className="text-fg-2 text-sm leading-relaxed m-0 font-sans tracking-tight line-clamp-2"
                />
                {/*
                  * The actions lead the footer; the run line sits under them in
                  * small grey type: when the workflow last ran, and any failed or stalled
                  * runs over the last 30 days as chips that open the runs behind
                  * them. The rest of the card opens the drawer on the totals.
                  */}
                <div className="mt-auto w-full pt-3 border-t border-fg/5 flex flex-col gap-2">
                    <div className="w-full flex flex-wrap items-center gap-2">
                        {action('Insights', <RocketLaunchIcon style={{ fontSize: 16 }} />, onViewInsights, true)}
                        {latestRun && (
                            <Tooltip title={describeRun(latestRun)} arrow placement="top" describeChild>
                                <span className="flex-1 basis-[6.5rem] inline-flex">
                                    {action('Backtest', <QueryStatsIcon style={{ fontSize: 16 }} />, () => onViewBacktest(latestRun))}
                                </span>
                            </Tooltip>
                        )}
                        {action('Customize', <SettingsIcon style={{ fontSize: 16 }} />, onCustomize)}
                    </div>
                    <WorkflowRunHealth
                        state={stats}
                        window={ATTENTION_WINDOW}
                        highlight={highlightBucket}
                        onSelectBucket={(bucket, attribution) => onOpenRuns({ bucket, attribution })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProjectWorkflowCard;
